import { useBaseForm } from '@pesto/hooks/src/useBaseForm';
import { useMutationWithToast } from '@pesto/ui';
import { Button, Typography } from '@pesto/ui';
import { TextField } from '@pesto/ui/components/Forms/TextField';
import { Separator } from '@pesto/ui/components/Separator';
import { Skeleton } from '@pesto/ui/components/Skeleton';
import { Form } from '@pesto/ui/features/Form/Form';
import { formatRate } from '@pesto/utils';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { useGetLoanQuery, useUpdateAprMarginMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';

const schema = z.object({
  aprMargin: z
    .string()
    .min(1, 'APR margin is required')
    .regex(/^\d+(\.\d+)?$/, 'Must be a valid number')
    .refine((val) => {
      const n = parseFloat(val);

      return n >= 0 && n <= 50;
    }, 'Must be between 0 and 50'),
  reason: z.string().min(1, 'Reason is required'),
});

type FormValues = z.infer<typeof schema>;

export const UpdateAprMarginComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { data: loanData, loading: loanLoading } = useGetLoanQuery({ variables: { id: loanId }, skip: !loanId });
  const { currentApr, aprMargin: currentAprMargin, currentPrimeRate, isVariableApr } = loanData?.loan ?? {};

  const { handleMutation } = useMutationWithToast();
  const [updateAprMargin, { loading }] = useUpdateAprMarginMutation(getLoanRefetchQuery(loanId));

  const {
    formData,
    formState,
    setValue,
    handleSubmit: formHandleSubmit,
  } = useBaseForm<FormValues>({
    schema,
    defaultValues: {
      aprMargin: '',
      reason: '',
    },
  });

  const primeRateValue = parseFloat((currentPrimeRate ?? '0').replace('%', ''));
  const newMarginValue = parseFloat(formData.aprMargin || '');
  const isValidMargin = !isNaN(newMarginValue) && newMarginValue >= 0 && newMarginValue <= 50;
  const computedNewApr = isValidMargin ? (newMarginValue + primeRateValue).toFixed(4) : null;

  const onSubmit = (data: FormValues) => {
    void handleMutation({
      mutation: () =>
        updateAprMargin({
          variables: {
            loanId,
            aprMargin: parseFloat(data.aprMargin),
            reason: data.reason,
          },
        }),
      extractSuccess: (result) => !!result?.data?.updateLoanAprMargin,
      onSuccess: () => api.close(),
      debugLabel: 'update-apr-margin',
      toast: {
        success: 'APR margin updated successfully',
        error: 'Failed to update APR margin',
        loading: 'Updating APR margin...',
      },
    });
  };

  const summaryFields = [
    { label: 'APR Type', value: isVariableApr == null ? '—' : isVariableApr ? 'Variable' : 'Fixed' },
    { label: 'Current APR', value: formatRate(currentApr) ?? '—' },
    { label: 'APR Margin', value: formatRate(currentAprMargin) ?? '—' },
    { label: 'Current Prime Rate', value: formatRate(currentPrimeRate) ?? '—' },
  ];

  if (loanLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {summaryFields.map(({ label, value }) => (
          <div key={label} className="flex flex-col gap-1">
            <Typography variant="bodySmall" className="text-muted-foreground text-xs font-medium">
              {label}
            </Typography>
            <Typography variant="body" className="text-sm font-semibold">
              {value}
            </Typography>
          </div>
        ))}
      </div>

      <Separator />

      <Form onSubmit={(event) => void formHandleSubmit(onSubmit)(event)} className="flex flex-col gap-4">
        <TextField
          label="New APR Margin (%)"
          value={formData.aprMargin}
          onChange={(e) => setValue('aprMargin', e.target.value)}
          placeholder="e.g. 12.5000"
          error={formState.errors.aprMargin?.message}
          required
        />

        {computedNewApr !== null && (
          <div className="bg-muted rounded-md p-3">
            <Typography variant="bodySmall" className="text-muted-foreground text-xs">
              Estimated New APR
            </Typography>
            <Typography variant="body" className="text-sm font-semibold">
              {computedNewApr}%
            </Typography>
            <Typography variant="bodySmall" className="text-muted-foreground text-xs">
              {newMarginValue}% margin + {primeRateValue}% prime rate
            </Typography>
          </div>
        )}

        <TextField
          label="Reason"
          value={formData.reason}
          onChange={(e) => setValue('reason', e.target.value)}
          placeholder="Reason for APR margin change"
          error={formState.errors.reason?.message}
          required
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => api.close()} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            Update APR Margin
          </Button>
        </div>
      </Form>
    </div>
  );
};
